import { Suspense, lazy } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { Sun, Moon } from 'lucide-react';
import { ErrorBoundary } from '../../shared/ErrorBoundary';

const VenueMap = lazy(() => import('./components/VenueMap'));

function VenueMapFallback() {
  return (
    <div className="flex items-center justify-center h-full min-h-[500px] px-6">
      <div className="max-w-md text-center">
        <h2 className="text-lg font-semibold text-stone-700 dark:text-stone-200 mb-2">
          Map unavailable
        </h2>
        <p className="text-sm text-stone-500 dark:text-stone-400">
          The venue map could not be loaded. A valid{' '}
          <code className="px-1 py-0.5 rounded bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300">
            VITE_MAPBOX_TOKEN
          </code>{' '}
          must be set in <code>frontend/.env</code> to display the map. See{' '}
          <code>frontend/.env.example</code> for the expected format.
        </p>
      </div>
    </div>
  );
}

export default function VenueMapPage() {
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-page flex flex-col">
      {/* Lightweight public header */}
      <header className="bg-bark-900 text-white">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-14">
          <div className="flex flex-col leading-tight">
            <span className="font-display italic font-bold text-lg">Bonsai Bar</span>
            <span className="text-bark-400 text-[10px] font-medium tracking-wide">Find a Workshop Near You</span>
          </div>
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-bark-300 hover:bg-bark-800 transition"
            aria-label="Toggle theme"
          >
            {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
          </button>
        </div>
      </header>

      {/* Map fills remaining space */}
      <div className="flex-1 relative">
        <ErrorBoundary fallback={<VenueMapFallback />}>
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full min-h-[500px]">
                <div className="text-stone-500 dark:text-stone-400">Loading map...</div>
              </div>
            }
          >
            <VenueMap />
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
}
